import {
  HENNOS_SSE_URL,
  HENNOS_POST_URL,
  HENNOS_HISTORY_URL,
  HENNOS_SEARCH_URL,
} from "../config";

class LifeforceBackend {
  private eventSource: EventSource | null = null;
  private accessToken: string | null = null;
  private _history: any[] | null = null;

  private historyPromise: Promise<any[] | null> | null = null;

  constructor() {
    console.log("LifeforceBackend initialized");
  }

  async register(access_token: string, callback: (event: any) => void) {
    if (this.eventSource) {
      console.warn("LifeforceBackend already initialized");
      return;
    }

    this.accessToken = access_token;
    console.log("LifeforceBackend connecting to SSE");
    const source = new EventSource(`${HENNOS_SSE_URL}/?token=${access_token}`);
    source.onmessage = (event) => {
      console.log("LifeforceBackend received SSE message:", event.data);
      callback(JSON.parse(event.data));
    };

    console.log("LifeforceBackend connected to SSE");
    this.eventSource = source;
  }

  async unregister() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
      console.log("LifeforceBackend disconnected from SSE");
    }
  }

  async event(event: { __type: string; value: any }) {
    if (!this.accessToken || !this.eventSource) {
      console.warn("LifeforceBackend not registered; cannot send event");
      return;
    }

    try {
      console.log("LifeforceBackend sending event:", event);
      const result = await fetch(`${HENNOS_POST_URL}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.accessToken}`,
        },
        body: JSON.stringify(event),
      });

      const json = await result.json();
      console.log("LifeforceBackend event response:", json);
    } catch (error) {
      console.error("LifeforceBackend event error:", error);
    }
  }

  async history() {
    if (!this.accessToken) {
      console.warn("LifeforceBackend not registered; cannot fetch history");
      return null;
    }

    if (this._history) return this._history;
    if (this.historyPromise) return this.historyPromise;

    this.historyPromise = this._fetchHistory();
    return this.historyPromise;
  }

  private async _fetchHistory() {
    if (!this.accessToken) {
      console.warn("LifeforceBackend not registered; cannot fetch history");
      return null;
    }
    try {
      console.log("LifeforceBackend fetching history");
      const result = await fetch(`${HENNOS_HISTORY_URL}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
        },
      });

      const json = await result.json();

      if (json.error) {
        console.error("LifeforceBackend history error:", json.error);
        return null;
      }

      if (!json.data) {
        console.warn("LifeforceBackend history: no data");
        return null;
      }

      console.log("LifeforceBackend fetched history:", json);
      this._history = json.data;
      return this._history;
    } catch (error) {
      console.error("LifeforceBackend history error:", error);
      return null;
    }
  }

  public async fetchSearchResults(
    query: string
  ): Promise<LifeforceSearchResults | null> {
    try {
      console.log("LifeforceBackend fetching search results for query:", query);
      const result = await fetch(
        `${HENNOS_SEARCH_URL}?q=${encodeURIComponent(query)}`,
        {
          method: "GET",
        }
      );

      const json = await result.json();

      if (json.error) {
        console.error("LifeforceBackend search error:", json.error);
        return null;
      }

      if (!json.data) {
        console.warn("LifeforceBackend search: no data");
        return null;
      }

      console.log("LifeforceBackend fetched search results:", json);
      return json.data;
    } catch (error) {
      console.error("LifeforceBackend search error:", error);
      return null;
    }
  }
}

type LifeforceSearchResults = {
  summary?: string;
  results?: any;
};

export default new LifeforceBackend();
